import { Component, OnInit, Input, ViewChild, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonSpinner, IonIcon, IonGrid, IonRow, IonCol, ToastController
 } from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ChartComponent, ApexChart, ApexPlotOptions, ApexAxisChartSeries, ApexDataLabels, ApexXAxis, ApexYAxis, ApexTooltip, ApexFill, ApexLegend, ApexTitleSubtitle } from 'ng-apexcharts';

import { 
    MarvMessageComponent, MarvButtonComponent, MarvTagComponent, MarvDrawerComponent, 
    MarvInputComponent, MarvSelectItem, MarvSelectComponent, MarvToggleComponent  
} from '@marv/components';
import { MarvValidatorsService } from '@marv/services';
import { environment } from '@app/environment';
import { 
    OperationCard, OperationHourConsumptionReport, AreaQuotaHourConsumptionReport, 
    CustomerSectorHourQuotaConsumptionReport, ProfileQuotaConsumptionReport,
    TransferableHourQuotaReport, TransferableCustomerHourOrderQuota 
} from '@app/models';
import { QuotaService, LoggedUserService } from '@app/services';

class SectorTransferQuotaInfos{
    name: string;
    oldQuota: number;
    newQuota: number;
}

class ChartQuotasOptions {
    chart: ApexChart;
    plotOptions: ApexPlotOptions;
    series: ApexAxisChartSeries; 
    dataLabels: ApexDataLabels;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    tooltip: ApexTooltip;
    title: ApexTitleSubtitle;
    fill: ApexFill;
    legend: ApexLegend;
};

@Component({
    selector: 'app-quotas-hour',
    templateUrl: './hour.component.html',
    styleUrls: ['./hour.component.scss'],
    imports: [
        CommonModule, IonSpinner, IonIcon, IonGrid, IonRow, IonCol, ReactiveFormsModule, TranslatePipe, ChartComponent,
        forwardRef(() => MarvMessageComponent), forwardRef(() => MarvButtonComponent), forwardRef(() => MarvTagComponent), 
        forwardRef(() => MarvDrawerComponent), forwardRef(() => MarvInputComponent), forwardRef(() => MarvSelectComponent),
        forwardRef(() => MarvToggleComponent)
    ]
})
export class QuotasHourComponent  implements OnInit {

    @Input({alias : 'operation', required : true}) operation: OperationCard;

    @ViewChild('drawerTransferQuota') drawerTransferQuota: MarvDrawerComponent;
    @ViewChild('drawerTransferQuotaGlobal') drawerTransferQuotaGlobal: MarvDrawerComponent;

    isFetching: boolean = false;
    isFetchingDownload: boolean = false;
    showQuotaAction: boolean = false;      
    showAreaTransfer: boolean = false;      
    reportQuota: OperationHourConsumptionReport|null;
    reportTransferableQuota: TransferableHourQuotaReport|null;
          
    displayAsDaysForm: FormGroup = new FormGroup({
        displayAsDays: new FormControl(false, [Validators.required])
    });

    chartQuotasOptions: ChartQuotasOptions|null = null;
    
    quotaTransferQuotaGlobal: TransferableCustomerHourOrderQuota|null = null;
    transferQuotaGlobalForm: FormGroup = new FormGroup({
        fromOperationId: new FormControl(null, [Validators.required]),
        fromQuotaId: new FormControl(null, [Validators.required]),
        toOperationId: new FormControl(null, [Validators.required]),
        toQuotaId: new FormControl(null, [Validators.required]),
        quotaQty: new FormControl(0, [Validators.required, Validators.min(1)]),
    });
    updateTransferQuotaGlobalFormFromOperationIdSubscription: Subscription;        
    updateTransferQuotaGlobalFormFromQuotaIdSubscription: Subscription;        
    updateTransferQuotaGlobalFormToOperationIdSubscription: Subscription;        
    updateTransferQuotaGlobalFormToQuotaIdSubscription: Subscription;        
    updateTransferQuotaGlobalFormQtySubscription: Subscription;     
       
    fromOperationsTransferQuotaGlobal: Array<MarvSelectItem> = [];
    fromQuotasTransferQuotaGlobal: Array<MarvSelectItem> = [];
    toOperationsTransferQuotaGlobal: Array<MarvSelectItem> = [];
    toQuotasTransferQuotaGlobal: Array<MarvSelectItem> = [];
    fromSectorTransferQuotaGlobalInfos: SectorTransferQuotaInfos|null = null;
    toSectorTransferQuotaGlobalInfos: SectorTransferQuotaInfos|null = null;   
    transferQuotaGlobalWarnings: Array<string> = [];
    transferQuotaGlobalError: string = '';
    
    fromAreaTransferQuota: AreaQuotaHourConsumptionReport|null = null;
    toAreaTransferQuota: AreaQuotaHourConsumptionReport|null = null;
    sectorTransferQuota: CustomerSectorHourQuotaConsumptionReport|null = null;
    profileTransferQuota: ProfileQuotaConsumptionReport|null = null;
    transferQuotaForm: FormGroup = new FormGroup({
        profileId: new FormControl(null, [Validators.required]),
        fromSectorId: new FormControl(null, [Validators.required]),
        toAreaId: new FormControl(null, [Validators.required]),
        toSectorId: new FormControl(null, [Validators.required]),
        quotaQty: new FormControl(0, [Validators.required, Validators.min(1)]),
    });
    updateTransferQuotaFormProfileIdSubscription: Subscription;
    updateTransferQuotaFormFromSectorIdSubscription: Subscription;
    updateTransferQuotaFormToAreaIdSubscription: Subscription;
    updateTransferQuotaFormToSectorIdSubscription: Subscription;
    updateTransferQuotaFormQtySubscription: Subscription;        
    profilesTransferQuota: Array<MarvSelectItem> = [];
    fromSectorsTransferQuota: Array<MarvSelectItem> = [];
    toAreasTransferQuota: Array<MarvSelectItem> = [];
    toSectorsTransferQuota: Array<MarvSelectItem> = [];
    transferQuotaWarnings: Array<string> = [];
    transferQuotaError: string = '';
    fromSectorTransferQuotaInfos: SectorTransferQuotaInfos|null = null;
    toSectorTransferQuotaInfos: SectorTransferQuotaInfos|null = null;   
    isFetchingTransferQuota: boolean = false;

    constructor(
        public translateService: TranslateService,
        private quotaService: QuotaService,
        private toastController: ToastController,
        private loggedUserService: LoggedUserService,
    ) { }

    async ngOnInit() {     
    
        this.displayAsDaysForm.controls['displayAsDays'].setValue(false);
       
        await this.loadQuotaReport();
        
        this.updateTransferQuotaGlobalFormFromOperationIdSubscription = this.transferQuotaGlobalForm.controls['fromOperationId'].valueChanges.subscribe(value => {  
            this.getFromQuotasTransferQuotaGlobal();
            this.setTransferQuotaGlobalNewValue();
        });
                
        this.updateTransferQuotaGlobalFormFromQuotaIdSubscription = this.transferQuotaGlobalForm.controls['fromQuotaId'].valueChanges.subscribe(value => {  
            this.setMaxQuotaTransferGlobal();
            this.setTransferQuotaGlobalNewValue();
        });
        
        this.updateTransferQuotaGlobalFormToOperationIdSubscription = this.transferQuotaGlobalForm.controls['toOperationId'].valueChanges.subscribe(value => {  
            this.getToQuotasTransferQuotaGlobal();
            this.setTransferQuotaGlobalNewValue();
        });
                
        this.updateTransferQuotaGlobalFormToQuotaIdSubscription = this.transferQuotaGlobalForm.controls['toQuotaId'].valueChanges.subscribe(value => {              
            this.setTransferQuotaGlobalNewValue();
        });
                
        this.updateTransferQuotaGlobalFormQtySubscription = this.transferQuotaGlobalForm.controls['quotaQty'].valueChanges.subscribe(value => {              
            this.setTransferQuotaGlobalNewValue();
        });
                
        this.updateTransferQuotaFormProfileIdSubscription = this.transferQuotaForm.controls['profileId'].valueChanges.subscribe(value => {            
            let profile: ProfileQuotaConsumptionReport|undefined = undefined; 
            if(this.transferQuotaForm.controls['profileId'].value && this.fromAreaTransferQuota && this.sectorTransferQuota){
                let sector = this.fromAreaTransferQuota.sectors.find(s => s.customerSector.id == this.transferQuotaForm.controls['fromSectorId'].value);   
                if(sector){
                    profile = sector.operationProfiles.find(p => p.operationProfile.id = this.transferQuotaForm.controls['profileId'].value);                    
                }            
            } 
            this.profileTransferQuota = (profile ? profile : null);
            this.fromSectorsTransferQuota = this.findSourceSectors();                    
            this.toSectorsTransferQuota = this.findDestSectors();
            this.setTransferQuotaNewValue();
        });
        
        this.updateTransferQuotaFormFromSectorIdSubscription = this.transferQuotaForm.controls['fromSectorId'].valueChanges.subscribe(value => {  
            let profile: ProfileQuotaConsumptionReport|undefined = undefined;           
            let sector: CustomerSectorHourQuotaConsumptionReport|undefined = undefined;           
            if(this.transferQuotaForm.controls['fromSectorId'].value && this.fromAreaTransferQuota){
                sector = this.fromAreaTransferQuota.sectors.find(s => s.customerSector.id == this.transferQuotaForm.controls['fromSectorId'].value);  
                if(sector){
                    profile = sector.operationProfiles.find(p => p.operationProfile.id = this.transferQuotaForm.controls['profileId'].value);                    
                }             
            }              
            this.sectorTransferQuota = (sector ? sector : null);
            this.profileTransferQuota = (profile ? profile : null);
            this.toSectorsTransferQuota = this.findDestSectors();
            this.setMaxQuotaTransfer();
            this.setTransferQuotaNewValue();
        });
        
        this.updateTransferQuotaFormToAreaIdSubscription = this.transferQuotaForm.controls['toAreaId'].valueChanges.subscribe(value => {    
            let area = this.reportQuota?.areas?.find(a => a.area.id === this.transferQuotaForm.controls['toAreaId'].value) || null;
            this.toAreaTransferQuota = area;            
            this.toSectorsTransferQuota = this.findDestSectors(); 
            this.setTransferQuotaNewValue();  
        });
        
        this.updateTransferQuotaFormToSectorIdSubscription = this.transferQuotaForm.controls['toSectorId'].valueChanges.subscribe(value => {            
           this.setTransferQuotaNewValue();
        });
        
        this.updateTransferQuotaFormQtySubscription = this.transferQuotaForm.controls['quotaQty'].valueChanges.subscribe(value => {            
            this.setTransferQuotaNewValue();
        });
    }
    
    ngOnDestroy() {     
          
        if(this.updateTransferQuotaGlobalFormFromOperationIdSubscription){
            this.updateTransferQuotaGlobalFormFromOperationIdSubscription.unsubscribe();
        }      
        if(this.updateTransferQuotaGlobalFormFromQuotaIdSubscription){
            this.updateTransferQuotaGlobalFormFromQuotaIdSubscription.unsubscribe();
        }              
        if(this.updateTransferQuotaGlobalFormToOperationIdSubscription){
            this.updateTransferQuotaGlobalFormToOperationIdSubscription.unsubscribe();
        }
        if(this.updateTransferQuotaGlobalFormToQuotaIdSubscription){
            this.updateTransferQuotaGlobalFormToQuotaIdSubscription.unsubscribe();
        }
        if(this.updateTransferQuotaGlobalFormQtySubscription){
            this.updateTransferQuotaGlobalFormQtySubscription.unsubscribe();
        }
        
        if(this.updateTransferQuotaFormProfileIdSubscription){
            this.updateTransferQuotaFormProfileIdSubscription.unsubscribe();
        }
        if(this.updateTransferQuotaFormFromSectorIdSubscription){
            this.updateTransferQuotaFormFromSectorIdSubscription.unsubscribe();
        }
        if(this.updateTransferQuotaFormToAreaIdSubscription){
            this.updateTransferQuotaFormToAreaIdSubscription.unsubscribe();
        }
        if(this.updateTransferQuotaFormToSectorIdSubscription){
            this.updateTransferQuotaFormToSectorIdSubscription.unsubscribe();
        }
        if(this.updateTransferQuotaFormQtySubscription){
            this.updateTransferQuotaFormQtySubscription.unsubscribe();
        }
    }
    
    async loadQuotaReport(){        
        this.isFetching = true;
        this.reportQuota = null;
        this.reportTransferableQuota = null;
        this.showQuotaAction = false;
        this.showAreaTransfer = false;
        let toAreasTransferQuota: Array<MarvSelectItem> = [];
        let loggedUser = this.loggedUserService.loggedUser;
        if(loggedUser){
            let report = await this.quotaService.loadHourQuotaReport(this.operation.id);
            if(report){
                let reportQuota = report.operationReports?.find(o => o.operation.id === this.operation.id);
                if(reportQuota){
                    this.reportQuota = reportQuota;
                    this.showQuotaAction = loggedUser.hierarchicalNature !== environment.USER_PROFIL_SECTOR;
                    this.showAreaTransfer = loggedUser.hierarchicalNature === environment.USER_PROFIL_FIELDTEAM;
                    
                    for(let area of this.reportQuota.areas){
                        toAreasTransferQuota.push(new MarvSelectItem(area.area.name, area.area.id));
                    }
                    
                }
            }
            
            this.reportTransferableQuota = await this.quotaService.loadTransferableHoursQuotaReport();
            this.getFromOperationsTransferQuotaGlobal();
            this.getToOperationsTransferQuotaGlobal();
        }
        this.drawChart();
        this.toAreasTransferQuota = toAreasTransferQuota;
        this.isFetching = false;
    }
    
    async drawChart(){
        let chartQuotasOptions: ChartQuotasOptions|null = null;
        if(this.reportQuota && this.reportQuota.globalReportLine){
            let gLine = this.reportQuota.globalReportLine;
            let series: ApexAxisChartSeries = [];
                
            series.push({
                name: this.translateService.instant('CONSUMED'),
                data: [Math.round(gLine.consumedAndRealisedQuota)],
                color: "rgba(33, 150, 243, 0.8)"
            });                
            series.push({
                name: this.translateService.instant('PLANNED_TO_COME'),
                data: [Math.round(gLine.consumedAndPlannedQuota - gLine.consumedAndRealisedQuota)],
                color: "rgba(156, 39, 176, 0.8)"
            });            
            if (gLine.availableQuota > 0) {                   
                series.push({
                    name: this.translateService.instant('BALANCE'),
                    data: [Math.round(gLine.availableQuota)],
                    color: "rgba(104, 159, 56, 0.8)"
                });                
            } 
            else {                       
                series.push({
                    name: this.translateService.instant('DEPASSEMENT'),
                    data: [Math.round(-gLine.availableQuota)],
                    color: "rgba(229, 57, 53, 0.8)"
                }); 
            }

            chartQuotasOptions = {
                chart: {
                    type: 'bar',
                    stacked: true,
                    height: 175,
                    toolbar: {
                        show: false
                    },
                    parentHeightOffset: 0
                },
                plotOptions: {
                    bar: {
                        horizontal: true,                        
                    },
                    
                },
                dataLabels: {                    
                    offsetY: -30,
                    style: {
                        colors: ['#222']
                    },
                    formatter: function (val, opt) {                        
                        return (typeof val === 'number' && val >= 1000 ? Math.floor(val/10)/100 + "K" : val);
                    },
                },
                title: {
                    text: this.translateService.instant('SECTOR_SUMMARY'),
                    align: 'center',
                    style: {
                        fontFamily: 'Roboto, "Helvetica Neue", sans-serif'
                    }
                },
                series: series,
                xaxis: {
                    categories: [this.translateService.instant('QUOTAS')],
                    labels: {
                        formatter: function(val) {
                            let v = parseInt(val.toString());
                            return (v >= 1000 ? Math.floor(v/10)/100 + "K" : val).toString();
                        }
                    }
                },
                yaxis: {                    
                    labels: {
                        show: false
                    },
                    
                },
                tooltip: {
                    
                },
                fill: {
                    opacity: 1
                },
                legend: {
                    position: "top",
                    horizontalAlign: "left",
                    offsetX: 10
                }
            }
        }
        this.chartQuotasOptions = chartQuotasOptions;
    }

    async downloadReport(){
        this.isFetchingDownload = true;
        let displayAsDays = this.displayAsDaysForm.controls['displayAsDays'].value || false;
        let fileUrl = await this.quotaService.loadExcelHourQuotaReport(this.operation.id, displayAsDays);
        let a = document.createElement('a'); 
        a.href = fileUrl;
        a.target = '_blank';
        a.click();
        this.isFetchingDownload = false;
    }
    
    getQuotaValue(value: number): number{
        return (this.displayAsDaysForm.controls['displayAsDays'].value ?  Math.round(value / 7) : value);
    }
    
    getFromOperationsTransferQuotaGlobal(){       
        let items: Array<MarvSelectItem> = [];
        this.transferQuotaGlobalForm.controls['fromOperationId'].setValue(null, {emitEvent: false});
        
        if(this.reportTransferableQuota !== null){
            this.reportTransferableQuota.lines.forEach(l => {
                items.push(new MarvSelectItem(l.operation.name, l.operation.id));
            });
        }
        
        this.fromOperationsTransferQuotaGlobal = items;
        if(items.length === 1){
            this.transferQuotaGlobalForm.controls['fromOperationId'].setValue(items[0].value, {emitEvent: false});
        }
        
        this.getFromQuotasTransferQuotaGlobal();
    }
    
    getFromQuotasTransferQuotaGlobal(){
        let items: Array<MarvSelectItem> = [];
        this.transferQuotaGlobalForm.controls['fromQuotaId'].setValue(null, {emitEvent: false});
        
        if(this.reportTransferableQuota !== null){                       
            let op = this.reportTransferableQuota.lines.find(l => l.operation.id == this.transferQuotaGlobalForm.controls['fromOperationId'].value);
            if(op){
                op.quota.forEach(q => {               
                    items.push(new MarvSelectItem(
                        q.customerSectorName+' - '+q.operationProfile.name+(q.availableQuota ? ' '+this.translateService.instant('MSG_QUOTAS_SECTOR_AVAILABLE_HOUR').replace('#quota_value#', q.availableQuota) : ''), 
                        q.customerOrderQuota
                    ));                
                });
            }            
        }
        
        this.fromQuotasTransferQuotaGlobal = items;
        if(items.length === 1){
            this.transferQuotaGlobalForm.controls['fromQuotaId'].setValue(items[0].value, {emitEvent: false});
        }
        this.setMaxQuotaTransferGlobal();
    }
        
    getToOperationsTransferQuotaGlobal(){
        let items: Array<MarvSelectItem> = [];
        this.transferQuotaGlobalForm.controls['toOperationId'].setValue(null, {emitEvent: false});
        
        if(this.reportTransferableQuota !== null){
            this.reportTransferableQuota.lines.forEach(l => {
                items.push(new MarvSelectItem(l.operation.name, l.operation.id));
            });
        }
        
        this.toOperationsTransferQuotaGlobal = items;
        if(items.length === 1){
            this.transferQuotaGlobalForm.controls['toOperationId'].setValue(items[0].value, {emitEvent: false});
        }        
        
        this.getToQuotasTransferQuotaGlobal();
    }
            
    getToQuotasTransferQuotaGlobal(){
        let items: Array<MarvSelectItem> = [];
        this.transferQuotaGlobalForm.controls['toQuotaId'].setValue(null, {emitEvent: false});
        
        if(this.reportTransferableQuota !== null){                       
            let op = this.reportTransferableQuota.lines.find(l => l.operation.id == this.transferQuotaGlobalForm.controls['toOperationId'].value);
            if(op){
                op.quota.forEach(q => {               
                    items.push(new MarvSelectItem(
                        q.customerSectorName+' - '+q.operationProfile.name+(q.availableQuota ? ' '+this.translateService.instant('MSG_QUOTAS_SECTOR_AVAILABLE_HOUR').replace('#quota_value#', q.availableQuota) : ''), 
                        q.customerOrderQuota
                    ));                
                });
            }            
        }
        
        this.toQuotasTransferQuotaGlobal = items;
        if(items.length === 1){
            this.transferQuotaGlobalForm.controls['toQuotaId'].setValue(items[0].value, {emitEvent: false});
        }
    }
    
    setMaxQuotaTransferGlobal(){
        let validators = [Validators.required, Validators.min(1)];
        let quotaTransferQuotaGlobal: TransferableCustomerHourOrderQuota|null = null;
        if(this.reportTransferableQuota !== null){ 
            let op = this.reportTransferableQuota.lines.find(l => l.operation.id == this.transferQuotaGlobalForm.controls['toOperationId'].value);
            if(op){
                let fromQuota = op.quota.find(q => q.customerOrderQuota == this.transferQuotaGlobalForm.controls['toQuotaId'].value);
                if(fromQuota){
                    quotaTransferQuotaGlobal = fromQuota;
                    validators.push(Validators.max(fromQuota.availableQuota));
                }
            }              
        }
        this.transferQuotaGlobalForm.controls['quotaQty'].setValidators(validators);    
        this.quotaTransferQuotaGlobal = quotaTransferQuotaGlobal;     
    }
    
    setTransferQuotaGlobalNewValue(){
        
        let fromSectorTransferQuotaGlobalInfos: SectorTransferQuotaInfos|null = null;
        let toSectorTransferQuotaGlobalInfos: SectorTransferQuotaInfos|null = null;
        
        let qty = parseInt(this.transferQuotaGlobalForm.controls['quotaQty'].value)
        if(!MarvValidatorsService.isInteger(qty)){
            qty = 0;
        }
        
        if(this.reportTransferableQuota !== null){ 
            let fromOp = this.reportTransferableQuota.lines.find(l => l.operation.id == this.transferQuotaGlobalForm.controls['fromOperationId'].value);
            if(fromOp){
                let fromQuota = fromOp.quota.find(q => q.customerOrderQuota == this.transferQuotaGlobalForm.controls['fromQuotaId'].value);
                if(fromQuota){
                    fromSectorTransferQuotaGlobalInfos = {
                        name: fromOp.operation.name+' - '+fromQuota.customerSectorName+' - '+fromQuota.operationProfile.name,
                        oldQuota: fromQuota.availableQuota,
                        newQuota: fromQuota.availableQuota - qty
                    };                        
                }
            } 
            
            let toOp = this.reportTransferableQuota.lines.find(l => l.operation.id == this.transferQuotaGlobalForm.controls['toOperationId'].value);
            if(toOp){
                let toQuota = toOp.quota.find(q => q.customerOrderQuota == this.transferQuotaGlobalForm.controls['toQuotaId'].value);
                if(toQuota){
                    toSectorTransferQuotaGlobalInfos = {
                        name: toOp.operation.name+' - '+toQuota.customerSectorName+' - '+toQuota.operationProfile.name,
                        oldQuota: toQuota.availableQuota,
                        newQuota: toQuota.availableQuota + qty
                    };                        
                }
            } 
        }
        
        this.fromSectorTransferQuotaGlobalInfos = fromSectorTransferQuotaGlobalInfos;
        this.toSectorTransferQuotaGlobalInfos = toSectorTransferQuotaGlobalInfos;
    }
    
    openDrawerTransferQuotaGlobal() {
        this.transferQuotaGlobalForm.controls['fromOperationId'].setValue(this.operation.id);
        this.drawerTransferQuotaGlobal?.open();
    }
    
    closeDrawerTransferQuotaGlobal(){
        this.drawerTransferQuotaGlobal?.close();
    }
    
    onDidDismissDrawerTransferQuotaGlobal($event: any){        
        this.quotaTransferQuotaGlobal = null;
        this.fromSectorTransferQuotaGlobalInfos = null;
        this.toSectorTransferQuotaGlobalInfos = null;    
        this.transferQuotaGlobalWarnings = [];    
        this.transferQuotaGlobalError = '';    
        this.transferQuotaForm.reset(); 
        this.getFromOperationsTransferQuotaGlobal();
        this.getToOperationsTransferQuotaGlobal();
    }
    
    async transferQuotaGlobal(){   
        this.isFetchingTransferQuota = true;   
        this.transferQuotaGlobalWarnings = [];    
        this.transferQuotaGlobalError = '';
        if(this.transferQuotaGlobalForm.valid){    
                
            let actionDone = await this.quotaService.submitQuotaHourTransferGlobal(this.transferQuotaGlobalForm.controls['fromQuotaId'].value, this.transferQuotaGlobalForm.controls['toQuotaId'].value, this.transferQuotaGlobalForm.controls['quotaQty'].value);
            if(actionDone?.success){   
                this.openDrawerTransferQuotaGlobal();
                const toast = await this.toastController.create({
                    message: this.translateService.instant('MSG_SUCCESS_QUOTAS_TRANSFER'),
                    duration: 6000,
                    position: 'top',
                    color: 'success'
                });
                await toast.present();
                await this.loadQuotaReport();
            }
            else if(actionDone?.warnings){
                this.transferQuotaGlobalWarnings = actionDone?.warnings;
            }
            else{
                this.transferQuotaGlobalError = this.translateService.instant('MSG_ERROR_QUOTAS_TRANSFER');  
            }
        }
        else{  
            
            if(this.quotaTransferQuotaGlobal && this.transferQuotaGlobalForm.controls['quotaQty'].value > this.quotaTransferQuotaGlobal.availableQuota){
                this.transferQuotaGlobalError = this.translateService.instant('MSG_ERROR_QUOTAS_TRANSFER_VALUE');
            }
            
            for (var controlName in this.transferQuotaGlobalForm.controls) {
                if(!this.transferQuotaGlobalForm.controls[controlName].valid) {
                    this.transferQuotaGlobalForm.controls[controlName].markAsTouched();
                }
            }
        }
        this.isFetchingTransferQuota = false;
    }
        
    openDrawerTransferQuota(area: AreaQuotaHourConsumptionReport, sector: CustomerSectorHourQuotaConsumptionReport, profile: ProfileQuotaConsumptionReport|null = null){        
        this.fromAreaTransferQuota = area;
        this.toAreaTransferQuota = area;
        this.sectorTransferQuota = sector;
        this.profilesTransferQuota = this.findProfiles();
        let uniqueProfil = (this.profilesTransferQuota.length === 1 ? sector.operationProfiles.find(o => o.operationProfile.id === this.profilesTransferQuota[0].value)  : null);
        this.profileTransferQuota = (profile ? profile : (uniqueProfil ? uniqueProfil : null)); 
        this.transferQuotaForm.controls['profileId'].setValue((profile ? profile.operationProfile.id : (uniqueProfil ? uniqueProfil.operationProfile.id : null)), {emitEvent: false});
        this.transferQuotaForm.controls['fromSectorId'].setValue(sector.customerSector.id, {emitEvent: false});
        this.transferQuotaForm.controls['toAreaId'].setValue(area.area.id, {emitEvent: false});
         
        this.fromSectorsTransferQuota = this.findSourceSectors();                    
        this.toSectorsTransferQuota = this.findDestSectors();     
        this.setMaxQuotaTransfer();       
        this.setTransferQuotaNewValue();   
        this.drawerTransferQuota?.open();
    }
    
    setMaxQuotaTransfer(){
        let validators = [Validators.required, Validators.min(1)];
        if(this.profileTransferQuota){
            let max = this.profileTransferQuota.availableQuota;    
            validators.push(Validators.max(max));
        }
        this.transferQuotaForm.controls['quotaQty'].setValidators(validators);         
    }
        
    closeDrawerTransferQuota(){
        this.drawerTransferQuota?.close();
    }
    
    onDidDismissDrawerTransferQuota($event: any){
        this.fromAreaTransferQuota = null;
        this.toAreaTransferQuota = null;
        this.sectorTransferQuota = null;
        this.profileTransferQuota = null;
        this.fromSectorTransferQuotaInfos = null;
        this.toSectorTransferQuotaInfos = null;
        this.profilesTransferQuota = [];
        this.fromSectorsTransferQuota = [];
        this.toSectorsTransferQuota = [];
        this.transferQuotaWarnings = [];
        this.transferQuotaError = '';
        this.transferQuotaForm.reset();   
        this.setMaxQuotaTransfer();
    }
    
    async transferQuota(){    
        if(this.fromAreaTransferQuota && this.sectorTransferQuota && this.profileTransferQuota){            
            this.transferQuotaWarnings = [];
            this.transferQuotaError = '';                
            if(this.transferQuotaForm.valid){    
                this.isFetchingTransferQuota = true;
                                
                let actionDone = await this.quotaService.submitQuotaHourTransfer(this.operation.id, this.transferQuotaForm.controls['profileId'].value, this.transferQuotaForm.controls['fromSectorId'].value, this.transferQuotaForm.controls['toSectorId'].value, this.transferQuotaForm.controls['quotaQty'].value);
                if(actionDone?.success){   
                    this.closeDrawerTransferQuota();
                    const toast = await this.toastController.create({
                        message: this.translateService.instant('MSG_SUCCESS_QUOTAS_TRANSFER'),
                        duration: 6000,
                        position: 'top',
                        color: 'success'
                    });
                    await toast.present();
                    await this.loadQuotaReport();
                }
                else if(actionDone?.warnings){
                    this.transferQuotaWarnings = actionDone?.warnings;
                }
                else{
                    this.transferQuotaError = this.translateService.instant('MSG_ERROR_QUOTAS_TRANSFER');  
                }
               
                this.isFetchingTransferQuota = false;
            }
            else{
                
                if(this.profileTransferQuota && this.transferQuotaForm.controls['quotaQty'].value > this.profileTransferQuota.availableQuota){
                    this.transferQuotaError = this.translateService.instant('MSG_ERROR_QUOTAS_TRANSFER_VALUE');
                }
                
                for (var controlName in this.transferQuotaForm.controls) {
                    if(!this.transferQuotaForm.controls[controlName].valid) {
                        this.transferQuotaForm.controls[controlName].markAsTouched();
                    }
                }
            }
        }
        
    }
    
    setTransferQuotaNewValue(){
        
        let fromSectorTransferQuotaInfos: SectorTransferQuotaInfos|null = null;
        let toSectorTransferQuotaInfos: SectorTransferQuotaInfos|null = null;
        
        let qty = parseInt(this.transferQuotaForm.controls['quotaQty'].value)
        if(!MarvValidatorsService.isInteger(qty)){
            qty = 0;
        }
        
        if(this.transferQuotaForm.controls['fromSectorId'].value && this.fromAreaTransferQuota){
            let sectorFrom = this.fromAreaTransferQuota.sectors.find(s => s.customerSector.id == this.transferQuotaForm.controls['fromSectorId'].value);   
            if(sectorFrom){
                let profile = sectorFrom.operationProfiles.find(p => this.profileTransferQuota?.operationProfile.id);
                if(profile){
                    fromSectorTransferQuotaInfos = {
                        name: sectorFrom.customerSector.name,
                        oldQuota: profile.availableQuota,
                        newQuota: profile.availableQuota - qty
                    };
                }
            }
        }
       
        if(this.transferQuotaForm.controls['toSectorId'].value && this.toAreaTransferQuota){
            let sectorTo = this.toAreaTransferQuota.sectors.find(s => s.customerSector.id == this.transferQuotaForm.controls['toSectorId'].value); 
            if(sectorTo){
                let profile = sectorTo.operationProfiles.find(p => this.profileTransferQuota?.operationProfile.id);
                if(profile){
                    toSectorTransferQuotaInfos = {
                        name: sectorTo.customerSector.name,
                        oldQuota: profile.availableQuota,
                        newQuota: profile.availableQuota + qty
                    };
                }
            }              
        }
                
        this.fromSectorTransferQuotaInfos = fromSectorTransferQuotaInfos;
        this.toSectorTransferQuotaInfos = toSectorTransferQuotaInfos;
    }
    
    findProfiles(): Array<MarvSelectItem> {
        let profiles: Array<MarvSelectItem> = [];
        if(this.fromAreaTransferQuota && this.fromAreaTransferQuota.sectors && this.fromAreaTransferQuota.sectors.length > 0){
            this.fromAreaTransferQuota.sectors[0].operationProfiles.forEach(p => {
                profiles.push(new MarvSelectItem(p.operationProfile.name, p.operationProfile.id));
            });
        }
        return profiles;
    }
    
    findSourceSectors(): Array<MarvSelectItem> {       
        let sectors: Array<MarvSelectItem> = [];
        if(this.reportQuota && this.fromAreaTransferQuota){
            this.fromAreaTransferQuota.sectors.forEach(sect => {
                let profile = sect.operationProfiles.find(p => this.profileTransferQuota?.operationProfile.id);
                if(profile){
                    if (profile.availableQuota != null && profile.availableQuota > 0) {
                        let label = sect.customerSector.name + ' ' + this.translateService.instant('MSG_QUOTAS_SECTOR_AVAILABLE_HOUR').replace('#quota_value#', profile.availableQuota);                                 
                        sectors.push(new MarvSelectItem(label, sect.customerSector.id));
                    }
                }
            })
        }
        
        if(!sectors.find(s => s.value === this.sectorTransferQuota?.customerSector.id)){
            this.sectorTransferQuota = null;
            this.transferQuotaForm.controls['fromSectorId'].setValue(null, {emitEvent: false});
        }
        
        return sectors;
    }

    findDestSectors(): Array<MarvSelectItem> {       
        let sectors: Array<MarvSelectItem> = [];
        if(this.toAreaTransferQuota){
            this.toAreaTransferQuota.sectors.forEach(sect => {
                if (sect.customerSector.id != this.sectorTransferQuota?.customerSector.id) {   
                    let profile = sect.operationProfiles.find(p => this.profileTransferQuota?.operationProfile.id);
                    if(profile){
                        let label = sect.customerSector.name + ' ' + this.translateService.instant('MSG_QUOTAS_SECTOR_ACTUAL_HOUR').replace('#quota_value#', profile.availableQuota);                      
                        sectors.push(new MarvSelectItem(label, sect.customerSector.id));
                    }
                }
            })
        };
        return sectors;
    }
    
}
